// src/components/admin/EditingAssetsCard.tsx
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileArchive, Trash2, Upload, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUploadFile } from '@/hooks/use-upload-file';
import { formatFilesize } from '@/lib/format';

/** One downloadable file offered to clippers who edit outside the platform. */
export interface EditingAsset {
  name: string;
  url: string;
  size?: number;
}

export interface EditingAssetsCardProps {
  value: EditingAsset[] | null;
  onChange: (v: EditingAsset[] | null) => void;
}

const EditingAssetsCard: React.FC<EditingAssetsCardProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { getUploadUrl, error } = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const assets = value ?? [];

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: EditingAsset[] = [];
      for (const file of files) {
        const url = await getUploadUrl(file);
        if (url) uploaded.push({ name: file.name, url, size: file.size });
      }
      if (uploaded.length > 0) onChange([...assets, ...uploaded]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const next = assets.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.campaigns.edit.editingAssets.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('admin.campaigns.edit.editingAssets.description')}
        </p>

        {assets.length > 0 && (
          <div className="space-y-2">
            {assets.map((asset, i) => (
              <div
                key={`${asset.url}-${i}`}
                data-testid={`asset-row-${i}`}
                className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
              >
                <FileArchive className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <a
                    href={asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium truncate block hover:underline"
                  >
                    {asset.name}
                  </a>
                  {asset.size != null && (
                    <span className="text-xs text-muted-foreground">{formatFilesize(asset.size)}</span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`remove-asset-${i}`}
                  onClick={() => handleRemove(i)}
                  className="text-destructive hover:text-destructive shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          data-testid="editing-assets-input"
          onChange={handleFiles}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {uploading
            ? t('admin.campaigns.edit.editingAssets.uploading')
            : t('admin.campaigns.edit.editingAssets.upload')}
        </Button>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
};

export default EditingAssetsCard;
